import { EventBus } from "../../contexts";
import { AuthContextValue } from "../../contexts/auth/types/auth";
import { logger, LOGS } from "../../utils";
import { ApiManager } from "../api";
import { UserLicensesDetails } from "../user";
import { UserProfileLoader } from "../user/UserProfileLoader";
import { LicenseState } from "./LicenseState";
import {
    AssignB2BUserLicenseParams,
    RemoveB2BUserLicenseParams,
} from "./types/license";
import { ActiveLicense, LicenseDetail } from "./types/license.api";
import {
    AddLicensesToOrganizationRequest,
    AffectOrgLicenseToUserResponse,
    CreateOrganizationRequestBody,
    LicenseAffectationStatus,
    ListAllUsersAndLicenceResponse,
    OrganizationResponse,
    RemoveOrgLicenseFromB2BUserResponse,
    UpdateUserAdminRoleResponse,
} from "./types/teamData.api";

export class LicenseManager {
    // #region Properties

    private eventBus: EventBus;

    private authContext: AuthContextValue;

    private profileLoader: UserProfileLoader;

    private apiManager: ApiManager;

    private licenseState: LicenseState;

    private allLicenseDetails: LicenseDetail[] = [];

    // #endregion Properties

    constructor(
        eventBus: EventBus,
        authContext: AuthContextValue,
        licenseState: LicenseState,
        profileLoader: UserProfileLoader,
        apiManager: ApiManager,
    ) {
        logger.info(LOGS.LicenseManager, "()");

        this.eventBus = eventBus;
        this.authContext = authContext;
        this.licenseState = licenseState;
        this.profileLoader = profileLoader;
        this.apiManager = apiManager;
    }

    // #region Initialisation and cleanup

    public async _initialize(): Promise<void> {
        logger.info(LOGS.LicenseManager, "initialize()");

        const fetchLicensesDetails =
            await this.apiManager.getAllLicenseDetails();
        this.allLicenseDetails = fetchLicensesDetails.licenseDetails;
    }

    /**
     * @method cleanup
     * Cleans up all resources and stops all active processes for the LicenseManager
     * ═══════════════════════════════════════════════════════════════════════════
     * @input {void}
     * @output {Promise<void>}
     * @public
     */
    public async _cleanup(): Promise<void> {
        logger.info(
            LOGS.LicenseManager,
            "LicenseManager.cleanup() - Starting cleanup process",
        );

        // Phase 1: Stop all active processes and timers
        try {
            logger.info(
                LOGS.LicenseManager,
                "  Phase 1: Stopping active processes",
            );

            // No timers, intervals, or polling to stop for LicenseManager
            // All operations are request-based
        } catch (error) {
            logger.error(
                LOGS.LicenseManager,
                `LicenseManager.cleanup() - Error in Phase 1: ${error}`,
            );
        }

        logger.info(
            LOGS.LicenseManager,
            "LicenseManager.cleanup() - Cleanup completed successfully",
        );
    }

    // #endregion Initialisation and cleanup

    // #region Public Methods

    //#region Enterprise_User Methods

    public async getAllUsersFromTenant(
        userTeamId: string,
    ): Promise<ListAllUsersAndLicenceResponse> {
        let allUsersList: any;
        try {
            allUsersList =
                await this.apiManager.listAllUsersAndLicence(userTeamId);
        } catch (error) {
            logger.error(
                LOGS.LicenseManager,
                `Error getting all users from tenant from apiManager : ${error}`,
            );
            throw new Error(
                `Error getting all users from tenant from apiManager : ${error}`,
            );
        }

        return allUsersList;
    }

    //#endregion Enterprise_User Methods

    //#region Enterprise_Manager Methods

    public async getTeamLicenseData(): Promise<OrganizationResponse> {
        logger.info(LOGS.LicenseManager, `getTeamLicenseData`);

        const userProfile = await this.profileLoader.getUserProfile();
        const teamId = userProfile.teamId;
        if (!teamId) {
            logger.warn(
                LOGS.LicenseManager,
                `No teamId found for user: ${userProfile.userId}`,
            );
            throw new Error(`No teamId found for user: ${userProfile.userId}`);
        }

        // Get team license data from apiManager
        let teamLicenseData: OrganizationResponse;
        try {
            teamLicenseData = await this.apiManager.getOrganization(teamId);
        } catch (error) {
            logger.error(
                LOGS.LicenseManager,
                `Error getting  TeamLicenseData from apiManager : ${error}`,
            );
            throw new Error(
                `Error getting TeamLicenseData from apiManager : ${error}`,
            );
        }

        // Check if team license data is valid
        if (!teamLicenseData) {
            logger.warn(
                LOGS.LicenseManager,
                `No team license data found for teamId: ${teamId}`,
            );
            throw new Error(`No team license data found for teamId: ${teamId}`);
        }

        return teamLicenseData;
    }

    /**
     * Affecte ou réaffecte un utilisateur B2B à un slot de licence d'organisation via l'API REST.
     * Détermine automatiquement s'il s'agit d'un assign ou d'un reassign en interrogeant
     * l'état actuel de l'organisation — le frontend n'a pas à connaître cette distinction.
     *
     * @param orgLicenseId - Id de l'OrganizationLicense cible (champ `id`, pas `licenseId`)
     * @param user         - { userId, userMail } de l'utilisateur à affecter
     */
    public async affectTeamLicenseToUser(
        orgLicenseId: string,
        user: AssignB2BUserLicenseParams,
    ): Promise<AffectOrgLicenseToUserResponse> {
        logger.info(
            LOGS.LicenseManager,
            `affectTeamLicenseToUsers(${user.userMail})`,
        );

        const userProfile = await this.profileLoader.getUserProfile();
        const teamId = userProfile.teamId;
        if (!teamId) {
            logger.warn(
                LOGS.LicenseManager,
                `No teamId found for user: ${userProfile.userId}`,
            );
            throw new Error(`No teamId found for user: ${userProfile.userId}`);
        }

        // Déterminer si l'utilisateur a déjà une licence active dans l'organisation
        const orgData = await this.apiManager.getOrganization(teamId);

        const hasActiveLicense = orgData.organizationLicenses.some((license) =>
            license.licensesAffectation.some(
                (affectation) =>
                    affectation.userMail?.toLowerCase() ===
                        user.userMail.toLowerCase() &&
                    affectation.status === LicenseAffectationStatus.ACTIVE,
            ),
        );

        logger.info(
            LOGS.LicenseManager,
            `affectTeamLicenseToUsers — hasActiveLicense: ${hasActiveLicense}`,
        );

        try {
            if (hasActiveLicense) {
                // Réaffectation : l'utilisateur change de slot de licence
                const result =
                    await this.apiManager.modifyOrganizationLicenseForB2BUser(
                        teamId,
                        {
                            userMail: user.userMail,
                            newOrgLicenseId: orgLicenseId,
                        },
                    );
                return {
                    instanceId: result.newInstanceId,
                    userMail: result.userMail,
                    isReassign: true,
                    // Snapshot post-mutation : org à jour + ligne user impactée → setQueryData sans refetch
                    organization: result.organization,
                    affectedUser: result.affectedUser,
                };
            } else {
                // Première affectation
                const result =
                    await this.apiManager.assignOrganizationLicenseToUser(
                        teamId,
                        {
                            userMail: user.userMail,
                            orgLicenseId: orgLicenseId,
                        },
                    );
                return {
                    instanceId: result.instanceId,
                    userMail: result.userMail,
                    isReassign: false,
                    // Snapshot post-mutation : org à jour + ligne user impactée → setQueryData sans refetch
                    organization: result.organization,
                    affectedUser: result.affectedUser,
                };
            }
        } catch (error) {
            logger.error(
                LOGS.LicenseManager,
                `Error affecting license to user ${user.userMail}: ${error}`,
            );
            throw new Error(
                `Error affecting license to user ${user.userMail}: ${error}`,
            );
        }
    }

    public async removeTeamLicenseFromUser(
        user: RemoveB2BUserLicenseParams,
    ): Promise<RemoveOrgLicenseFromB2BUserResponse> {
        logger.info(
            LOGS.LicenseManager,
            `removeTeamLicenseFromUser(${user.userMail})`,
        );

        const userProfile = await this.profileLoader.getUserProfile();
        const teamId = userProfile.teamId;
        if (!teamId) {
            logger.warn(
                LOGS.LicenseManager,
                `No teamId found for user: ${userProfile.userId}`,
            );
            throw new Error(`No teamId found for user: ${userProfile.userId}`);
        }

        try {
            const result =
                await this.apiManager.removeOrganizationLicenseFromB2BUser(
                    teamId,
                    {
                        userMail: user.userMail,
                        orgLicenseId: user.orgLicenseId,
                    },
                );
            return result;
        } catch (error) {
            logger.error(
                LOGS.LicenseManager,
                `Error removing license from user ${user.userMail}: ${error}`,
            );
            throw new Error(
                `Error removing license from user ${user.userMail}: ${error}`,
            );
        }
    }

    public async setUserTeamAdmin(
        userId: string,
        userMail: string,
        setAdmin: boolean,
    ): Promise<UpdateUserAdminRoleResponse> {
        logger.info(
            LOGS.LicenseManager,
            `setUserTeamAdmin(${userId}, ${setAdmin})`,
        );

        const userProfile = await this.profileLoader.getUserProfile();
        const teamId = userProfile.teamId;
        if (!teamId) {
            logger.warn(
                LOGS.LicenseManager,
                `No teamId found for user: ${userProfile.userId}`,
            );
            throw new Error(`No teamId found for user: ${userProfile.userId}`);
        }

        try {
            const result = await this.apiManager.updateUserAdminRole(teamId, {
                userId,
                userMail,
                setAdmin,
            });

            // Refresh forcé du token Keycloak si le backend signale que le rôle realm de
            // l'utilisateur courant vient d'être modifié (le caller a changé ses propres droits admin).
            // Garantit que le JWT reflète les nouveaux rôles avant la suite du flow.
            if (result.tokenRefreshRequired) {
                logger.info(
                    LOGS.LicenseManager,
                    `setUserTeamAdmin - tokenRefreshRequired=true → demande de refresh du token`,
                );
                await this.authContext.forceRefresh();
            }

            return result;
        } catch (error) {
            logger.error(
                LOGS.LicenseManager,
                `Error setting user admin role: ${error}`,
            );
            throw new Error(`Error setting user admin role: ${error}`);
        }
    }

    //#endregion Enterprise_Manager Methods

    //#region Mailvista Administration Methods

    public async _createUnknownUser(): Promise<boolean> {
        logger.info(LOGS.LicenseManager, "_createUnknownUser()");

        const fakeSuccess = true;

        return fakeSuccess;
    }

    public async createTeam(
        teamDataBody: CreateOrganizationRequestBody,
    ): Promise<OrganizationResponse> {
        logger.info(
            LOGS.LicenseManager,
            `createTeam() with body ${JSON.stringify(teamDataBody)}`,
        );

        try {
            const result =
                await this.apiManager.createOrganization(teamDataBody);
            return result;
        } catch (error) {
            logger.error(
                LOGS.LicenseManager,
                `Error creating team from apiManager : ${error}`,
            );
            throw new Error(`Error creating team from apiManager : ${error}`);
        }
    }

    public async expireTeamLicense(
        teamId: string,
        orgLicenseId: string,
        expirationDate: string,
    ): Promise<OrganizationResponse> {
        logger.info(LOGS.LicenseManager, "expireTeamLicense()");

        try {
            const expirationDateBody = {
                expirationDate,
            };

            const result =
                await this.apiManager.setOrganizationLicenseExpirationDate(
                    teamId,
                    orgLicenseId,
                    expirationDateBody,
                );
            return result;
        } catch (error) {
            logger.error(
                LOGS.LicenseManager,
                `Error expiring license ${orgLicenseId} for team ${teamId} from apiManager : ${error}`,
            );
            throw new Error(
                `Error expiring license ${orgLicenseId} for team ${teamId} from apiManager : ${error}`,
            );
        }
    }

    public async addTeamLicense(
        teamId: string,
        licenses: AddLicensesToOrganizationRequest[],
    ): Promise<OrganizationResponse> {
        logger.info(LOGS.LicenseManager, "addTeamLicense()");

        try {
            const result = await this.apiManager.addLicensesToOrganization(
                teamId,
                licenses,
            );
            return result;
        } catch (error) {
            logger.error(
                LOGS.LicenseManager,
                `Error adding license(s) to team ${teamId} from apiManager : ${error}`,
            );
            throw new Error(
                `Error adding license(s) to team ${teamId} from apiManager : ${error}`,
            );
        }
    }

    //#endregion Mailvista Administration Methods

    /**
     * @method getFullLicensesDetails
     * Retourne les détails complets des licences.
     * Utilise le cache si disponible (alimenté par LicenseState.seedFromBootstrap au startup).
     * Effectue un appel à GET /me/license uniquement si le cache est vide (refresh manuel).
     * ═══════════════════════════════════════════════════════════════════════════
     * @returns {Promise<UserLicensesDetails>}
     */
    public async getFullLicensesDetails(): Promise<UserLicensesDetails> {
        logger.info(LOGS.LicenseManager, "getFullLicensesDetails()");

        // Retourner le cache s'il est disponible (cas nominal : alimenté au bootstrap)
        const cachedLicensesDetails = this.licenseState.details;
        if (cachedLicensesDetails !== null) {
            return cachedLicensesDetails;
        }

        // Fallback : refresh manuel (appelé avant bootstrap ou après invalidation du cache)
        const [allUserLicenses] = await Promise.all([
            this.apiManager.fetchLicensesDetails(),
            this.profileLoader.getUserProfile(),
        ]);

        if (!allUserLicenses) {
            throw new Error("No licence found from api");
        }

        this.licenseState.set(allUserLicenses);

        return this.licenseState.details!;
    }

    public hasValidLicense = (): boolean => this.licenseState.hasValidLicense();

    /**
     * @method getActiveMonthlyAnalysedMailsLimit
     * Retourne le nombre d'emails analysés autorisés mensuellement par la licence active
     * ═══════════════════════════════════════════════════════════════════════════
     * @returns {Promise<number>} Le nombre d'emails analysés autorisés par mois
     */
    public async getActiveMonthlyAnalysedMailsLimit(): Promise<number> {
        logger.info(
            LOGS.LicenseManager,
            "getActiveMonthlyAnalysedMailsLimit()",
        );

        try {
            const fullLicensesDetails = await this.getFullLicensesDetails();

            if (!this.hasValidLicense()) {
                logger.info(
                    LOGS.LicenseManager,
                    "Aucune licence active trouvée",
                );
                return 0;
            }

            const maxMailLimit = Math.max(
                0,
                ...fullLicensesDetails.active.map(
                    (l) => l.detail.monthlyLimit.byAnalysedMails,
                ),
            );

            logger.info(
                LOGS.LicenseManager,
                `Limite mensuelle d'emails analysés: ${maxMailLimit}`,
            );
            return maxMailLimit;
        } catch (error) {
            logger.error(
                LOGS.LicenseManager,
                `Erreur lors de la récupération de la limite d'emails analysés: ${error}`,
            );
            throw new Error(
                `Erreur lors de la récupération de la limite d'emails analysés: ${error}`,
            );
        }
    }

    public getLicenseDetailFromId(licenseId: string): LicenseDetail {
        const licenseDetails = this.allLicenseDetails.find(
            (ld) => ld.id === licenseId,
        );

        if (!licenseDetails) {
            logger.error(
                [LOGS.LicenseManager],
                `Error getting licenseDetails for licenseId ${licenseId}`,
            );
            throw new Error(
                `Error getting licenseDetails for licenseId ${licenseId}`,
            );
        }

        return licenseDetails;
    }

    /**
     * @method getUserLicenseProfile
     * @deprecated Utiliser getActiveLicenses() à la place
     */
    public getUserLicenseProfile(): ActiveLicense[] | undefined {
        logger.warn(
            LOGS.LicenseManager,
            "DEPRECATED getUserLicenseProfile() - Use getLicenseProfile() instead",
        );
        return this.licenseState.details?.active ?? undefined;
    }

    /**
     * @method getLicenseProfile
     * Retourne le profil de licence actuel de l'utilisateur
     * ═══════════════════════════════════════════════════════════════════════════
     * @input {void}
     * @output {ActiveLicense[]} Liste des licences actives (vide si non initialisé)
     */
    public getLicenseProfile(): ActiveLicense[] {
        logger.info(LOGS.LicenseManager, "getLicenseProfile()");

        const licenseProfile = this.licenseState.details?.active ?? [];

        return licenseProfile;
    }

    // #endregion Public Methods
}
